import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';

const BookingButton = ({ variant = 'primary', size = 'default', className = '', label = 'Book Session' }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const variants = {
    primary: "bg-gradient-to-r from-indigo-600 to-cyan-500 text-white hover:from-indigo-700 hover:to-cyan-600 focus:ring-indigo-500",
    secondary: "border-2 border-cyan-400 text-cyan-100 hover:bg-white hover:text-slate-900 focus:ring-cyan-400",
    outline: "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-gray-500"
  };

  const sizes = {
    small: "px-3 py-1.5 text-sm",
    default: "px-4 py-2 text-sm",
    large: "px-6 py-3 text-base"
  };

  const handleBookingClick = () => {
    // This would typically open a Calendly modal or redirect to booking page
    window.open('https://calendly.com/dukelester4/one-on-one-session', '_blank');
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleBookingClick}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      aria-label={label}
    >
      <CalendarDaysIcon className="h-4 w-4 mr-2" />
      {label}
    </motion.button>
  );
};

export const BookingCard = ({ title, description, price, duration, features }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {description}
        </p>
        <div className="text-3xl font-bold text-indigo-600 dark:text-cyan-300 mb-2">
          {price}
        </div>
        <div className="flex items-center justify-center text-gray-500 dark:text-gray-500">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span className="text-sm">{duration}</span>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
          What's included:
        </h4>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <BookingButton 
        variant="primary" 
        size="large" 
        label="Reserve & Continue to Payment"
        className="w-full"
      />
    </motion.div>
  );
};

export const SessionTypeCard = ({ type, icon, title, description, price, duration }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-6 hover:shadow-xl transition-all duration-300 cursor-pointer"
    >
      <div className="text-center">
        <div className="text-4xl mb-4">{icon}</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
          {description}
        </p>
        <div className="text-2xl font-bold text-indigo-600 dark:text-cyan-300 mb-2">
          {price}
        </div>
        <div className="flex items-center justify-center text-gray-500 dark:text-gray-500 mb-4">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span className="text-sm">{duration}</span>
        </div>
        <BookingButton variant="primary" size="default" label="Book This Session" />
      </div>
    </motion.div>
  );
};

export default BookingButton;
